import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, IconButton, Button, Chip, Tooltip, Alert } from '@mui/material';
import { ArrowBack, Close, KeyboardArrowDown } from '@mui/icons-material';
import ProjectDetails from '../components/projects/ProjectDetails';
import { getProjectById, Project } from '../services/projectService';
import { getBoardUrlForProject } from '../services/collectifileService';
import RulerIcon from '../components/icons/RulerIcon';
import type { ProjectDisplayData } from './ProjectsPage';
import AddProjectForm from '../components/projects/AddProjectForm';
import { useProjectCameraHlsUrls } from '../hooks/useProjectCameraHlsUrls';
import { resolveProjectIdForHardwareQuery } from '../utils/projectCameraStreams';

const toDisplayData = (p: Project): ProjectDisplayData => {
  const start = p.start_date ? new Date(p.start_date) : new Date();
  const end = p.end_date ? new Date(p.end_date) : start;
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return {
    // wymagane przez ProjectDetails
    id: p.project_id || '',
    name: p.project_name || '',
    // obliczenia i metryki
    days,
    progress: 0,
    wwp: 0,
    wwh: 0,
    sales: Number(p.sales || 0),
    pozDz: Number((p as any).PDZ || p.PDZ || 0),
    costs: Number(p.costs || 0),
    profit: Number(p.sales || 0) - Number(p.costs || 0),
    ros: (() => {
      const sales = Number(p.sales || 0);
      const profit = sales - Number(p.costs || 0);
      return sales ? Math.round((profit / sales) * 100) : 0;
    })(),
    // zgodność wsteczna
    manager: p.responsible_person1 || '',
    startDate: p.start_date || '',
    endDate: p.end_date || '',
    planBud: Number(p.budget || 0),
    driveLink: p.google_drive_link,
    miroLink: p.miro_link,
    folderId: undefined,
    // pola z Project
    project_id: p.project_id,
    project_name: p.project_name,
    value: Number(p.value || 0),
    budget: Number(p.budget || 0),
    client_name: p.client_name,
    contract_number: p.contract_number,
    contract_sign_date: p.contract_sign_date,
    contract_subject: p.contract_subject,
    investment_address: p.investment_address,
    location_lat: p.location_lat,
    location_lng: p.location_lng,
    geocoded_at: p.geocoded_at,
    geocode_provider: p.geocode_provider,
    kzm_emails: p.kzm_emails,
    responsible_person1: p.responsible_person1,
    responsible_person2: p.responsible_person2,
    responsiblePerson1: p.responsiblePerson1,
    responsiblePerson2: p.responsiblePerson2,
    warranty_period_months: p.warranty_period_months,
    google_drive_link: p.google_drive_link,
    miro_link: p.miro_link,
    work_completion_deadline: p.work_completion_deadline,
    project_phase: p.project_phase,
    PDZ: (p as any).PDZ,
    // dodatkowe linki szablonów
    contract_draft_1: p.contract_draft_1,
    contract_draft_2: p.contract_draft_2,
    kzm_draft: p.kzm_draft,
    pokr_draft: p.pokr_draft,
    pocr_draft: p.pocr_draft,
    collectifile_board_id: p.collectifile_board_id,
  } as ProjectDisplayData;
};

const ProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [project, setProject] = React.useState<ProjectDisplayData | null>(null);
  const [editOpen, setEditOpen] = React.useState(false);
  const [headerTab, setHeaderTab] = React.useState(0);
  const detailsRef = React.useRef<any>(null);
  const headerTabs = ['Szczegóły','Kontraktowanie','Plan zamówień','Harmonogram','KZM','Przepływy pieniężne','Materiały','Usługi','Podwykonawcy'];
  // Ikony obok przycisku – obsługa kamer (jak w tabeli)
  const [cameraOpen, setCameraOpen] = React.useState(false);
  // Zawsze ładuj /hardware dla tego projektu — miniaturki w ProjectDetails potrzebują URL-i z proxy,
  // a nie pustego / przestarzałego fallbacku magazynu.
  const cameraHardwareProjectId = resolveProjectIdForHardwareQuery(project);
  const {
    urls: cameraMagazynUrls,
    loading: cameraHardwareLoading,
    hasHardwareCameras,
    hardwareDevices: cameraHardwareDevices,
  } = useProjectCameraHlsUrls(Boolean(cameraHardwareProjectId), cameraHardwareProjectId);
  const cameraStreamUrls = React.useMemo(
    () => cameraMagazynUrls.filter((u) => String(u || '').trim()),
    [cameraMagazynUrls]
  );
  const camDetailGridCols = cameraStreamUrls.length <= 1 ? 1 : 2;
  const [cameraDetailPlayerEpoch, setCameraDetailPlayerEpoch] = React.useState(0);
  React.useEffect(() => {
    if (!cameraOpen) return;
    setCameraDetailPlayerEpoch((e) => e + 1);
  }, [cameraOpen, cameraHardwareProjectId]);

  const renderPhaseChip = (phase?: string) => {
    const text = '#020242';
    const norm = (phase || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, ''); // usunięcie polskich znaków dla pewnego dopasowania

    // Kolory zgodnie z tabelą ofert:
    // - "w trakcie" => #E6F0FF (niebieski pastel)
    // - "wygrana"   => #E6F5E6 (zielony pastel)
    // Dodatkowo:
    // - "W realizacji" (dla projektów) => #FEEED5
    const palette =
      norm === 'w realizacji'
        ? { bg: '#FEEED5', fg: text, label: 'W realizacji' }
        : norm === 'zakonczono w okresie gwarancji' || norm === 'zakonczone w okresie gwarancji'
        ? { bg: '#E6F0FF', fg: text, label: 'Zakończono w okresie gwarancji' }
        : norm === 'zakonczone po gwarancji'
        ? { bg: '#E6F5E6', fg: text, label: 'Zakończone po gwarancji' }
        : { bg: '#F1F1F6', fg: text, label: (phase || '—') };
    return (
      <Chip
        label={palette.label}
        size="small"
        sx={{
          bgcolor: palette.bg,
          color: palette.fg,
          borderRadius: '4px',
          minHeight: 28,
          height: 28,
          '& .MuiChip-label': {
            px: 1,
            py: 0,
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '16px',
            color: palette.fg
          }
        }}
      />
    );
  };

  React.useEffect(() => {
    let active = true;
    (async () => {
      try {
        if (!id) { setError('Brak identyfikatora projektu'); setLoading(false); return; }
        const data = await getProjectById(id);
        if (!active) return;
        if (!data) { setError('Nie znaleziono projektu'); setLoading(false); return; }
        setProject(toDisplayData(data));
      } catch (e: any) {
        setError(e?.message || 'Błąd podczas pobierania projektu');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id]);

  // Deep-link: ?open=packages|inquiries lub ?section=contracting → przełącz na „Kontraktowanie”
  React.useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const open = (sp.get('open') || '').toLowerCase();
      const section = (sp.get('section') || '').toLowerCase();
      if (open === 'packages' || open === 'inquiries' || section === 'contracting') {
        setHeaderTab(1);
        try { detailsRef.current?.setTab?.(1); } catch {}
      }
    } catch {}
  }, []);

  // Po załadowaniu projektu upewnij się, że Kontraktowanie jest aktywne przy ?open=packages/inquiries
  React.useEffect(() => {
    if (!project) return;
    try {
      const sp = new URLSearchParams(window.location.search);
      const open = (sp.get('open') || '').toLowerCase();
      const section = (sp.get('section') || '').toLowerCase();
      if (open === 'packages' || open === 'inquiries' || section === 'contracting') {
        setHeaderTab(1);
        try { detailsRef.current?.setTab?.(1); } catch {}
      }
    } catch {}
  }, [project]);

  // Uwaga: globalne skróty do zakładek głównych są obsługiwane w Sidebar (klawisz §/£).
  // Ten komponent tylko renderuje semantyczne atrybuty potrzebne do ich działania.

  // Skrót klawiaturowy: 'u' – przejście do listy projektów
  React.useEffect(() => {
    function isEditable(el: EventTarget | null): boolean {
      const e = el as HTMLElement | null;
      if (!e) return false;
      const tag = (e.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || (e as any).isContentEditable) return true;
      let p: HTMLElement | null = e.parentElement;
      while (p) {
        const t = (p.tagName || '').toLowerCase();
        if (t === 'input' || t === 'textarea' || (p as any).isContentEditable) return true;
        p = p.parentElement;
      }
      return false;
    }
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== 'u' && ev.code !== 'KeyU') return;
      if (ev.ctrlKey || ev.metaKey || ev.altKey || ev.shiftKey) return;
      if (isEditable(ev.target)) return; // nie przełączaj podczas pisania
      ev.preventDefault();
      navigate('/projects');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2 }}>
        <CircularProgress size={24} />
        <Typography>Ładowanie projektu…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <IconButton size="small" onClick={() => navigate(-1)} aria-label="Wstecz" sx={{ mb: 1 }}>
          <ArrowBack fontSize="small" />
        </IconButton>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box sx={{ p: 2 }}>
        <IconButton size="small" onClick={() => navigate(-1)} aria-label="Wstecz" sx={{ mb: 1 }}>
          <ArrowBack fontSize="small" />
        </IconButton>
        <Typography>Projekt nie został znaleziony.</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ bgcolor: '#FAFAFA', mt: '20px', position: 'relative' }}>
        {/* Wróć do projektów – nad kontenerem, bez przesuwania układu */}
        <Box
          onClick={() => navigate('/projects')}
          sx={{
            position: 'absolute',
            top: '-24px',
            left: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            cursor: 'pointer',
            userSelect: 'none'
          }}
          aria-label="Wróć do projektów"
          role="button"
        >
          <KeyboardArrowDown sx={{ transform: 'rotate(90deg)', width: 16, height: 16, color: '#1F1FB0' }} />
          <Typography
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              lineHeight: '16px',
              fontWeight: 500,
              color: '#1F1FB0'
            }}
          >
            Wróć do projektów
          </Typography>
        </Box>
        {/* Wiersz nagłówka zgodny z pozycją nagłówka na stronie projektów */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0, mb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '20px',
                lineHeight: '24px',
                fontWeight: 400,
                color: '#020242',
                m: 0
              }}
            >
              {`${(project.project_id || (typeof project.id === 'string' ? project.id.split(' ')[0] : ''))} ${project.name}`.trim()}
            </Typography>
            {renderPhaseChip(project.project_phase)}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Ikony: Drive, Kamery, Board – po lewej od przycisku, z marginesem 24px */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mr: '24px' }}>
              {/* Google Drive */}
              {project.google_drive_link ? (
                <Tooltip title="Dysk">
                  <IconButton
                    size="small"
                    component="a"
                    href={project.google_drive_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ p: 0.25, width: 32, height: 32 }}
                    aria-label="Dysk Google"
                  >
                    <Box component="img" src="/assets/icons/drive.svg" alt="Dysk" />
                  </IconButton>
                </Tooltip>
              ) : null}
              {/* Kamery – jak w tabeli: prosty overlay z podglądem */}
              <Tooltip title="Kamery">
                <IconButton
                  size="small"
                  onClick={() => setCameraOpen(true)}
                  sx={{ p: 0.25, width: 32, height: 32 }}
                  aria-label="Kamery"
                >
                  <Box component="img" src="/assets/icons/camera.svg" alt="Kamery" />
                </IconButton>
              </Tooltip>
              {/* Board */}
              {project.project_id ? (
                <Tooltip title="Board">
                  <IconButton
                    size="small"
                    component="a"
                    href={getBoardUrlForProject(project.project_id, project.project_name || project.name, (project as any).collectifile_board_id, project.miro_link)}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ p: 0.25, width: 32, height: 32 }}
                    aria-label="Board"
                  >
                    <RulerIcon filled={!!(project as any).collectifile_board_id || !!project.miro_link} sx={{ width: 24, height: 24 }} />
                  </IconButton>
                </Tooltip>
              ) : null}
              {/* CAD (DWG/DXF) */}
              {project.project_id ? (
                <Tooltip title="CAD (DWG/DXF)">
                  <IconButton
                    size="small"
                    component="a"
                    href={`/cad/${encodeURIComponent(project.project_id)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ p: 0.25, width: 32, height: 32 }}
                    aria-label="CAD"
                  >
                    <Box component="img" src="/assets/icons/architecture.svg" alt="CAD" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Box>
            <Button
              variant="contained"
              onClick={() => setEditOpen(true)}
              sx={{
                width: 116,
                height: 40,
                px: 1,
                whiteSpace: 'nowrap',
                borderRadius: '4px',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 600,
                backgroundColor: '#4040DB',
                color: '#fff',
                '&:hover': { backgroundColor: '#1F1FB0' },
                '&:active': { backgroundColor: '#1A1A98' }
              }}
            >
              Edytuj projekt
            </Button>
          </Box>
        </Box>

        {/* Nowy pasek zakładek pod nagłówkiem */}
        <Box
          data-tab-scope="main"
          sx={{ mt: '28px', mb: 2, borderBottom: '1px solid #EDEDED', display: 'flex', gap: 3, alignItems: 'flex-end' }}
        >
          {headerTabs.map((label, idx) => (
            <Box
              key={label}
              data-tab="main"
              aria-selected={headerTab === idx ? 'true' : 'false'}
              onClick={() => { setHeaderTab(idx); try { detailsRef.current?.setTab?.(idx); } catch {} }}
              sx={{
                position: 'relative',
                pb: 1,
                cursor: 'pointer',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '16px',
                color: '#020242',
                fontWeight: headerTab === idx ? 500 : 400,
                userSelect: 'none'
              }}
            >
              {label}
              {headerTab === idx && (
                <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: -1, height: 2, bgcolor: '#000000' }} />
              )}
            </Box>
          ))}
        </Box>

        <ProjectDetails
          ref={detailsRef}
          project={project}
          onTabChange={(i)=>setHeaderTab(i)}
          onOpenCameras={() => setCameraOpen(true)}
          cameraPreviewUrls={cameraMagazynUrls}
        />
        {/* Overlay z kamerami (prosty podgląd jak w tabeli) */}
        {cameraOpen && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              bgcolor: 'rgba(0,0,0,0.6)',
              zIndex: 1400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2
            }}
            onClick={() => setCameraOpen(false)}
          >
            <Box
              sx={{
                position: 'relative',
                width: 'min(1200px, 96vw)',
                height: 'min(700px, 80vh)',
                bgcolor: '#111',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                display: 'flex',
                flexDirection: 'column',
              }}
              onClick={(e)=> e.stopPropagation()}
            >
              <IconButton
                onClick={() => setCameraOpen(false)}
                aria-label="Zamknij"
                sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, color: '#fff' }}
              >
                <Close />
              </IconButton>
              {cameraHardwareLoading && (
                <Box sx={{ position: 'absolute', top: 10, left: 10, zIndex: 2, display: 'flex', alignItems: 'center', gap: 1, pointerEvents: 'none' }}>
                  <CircularProgress size={18} sx={{ color: '#fff' }} />
                  <Typography variant="caption" sx={{ color: '#ccc' }}>Ładowanie urządzeń…</Typography>
                </Box>
              )}
              {!cameraHardwareLoading && cameraHardwareDevices.length === 0 && (
                <Box sx={{ position: 'absolute', bottom: 8, left: 8, right: 48, zIndex: 2, maxHeight: '36%', overflow: 'auto' }}>
                  <Alert severity="info" sx={{ py: 0.5, '& .MuiAlert-message': { py: 0 } }}>
                    Brak urządzenia w Ustawieniach → Hardware dla tego projektu. Używane są domyślne adresy magazynowe.
                  </Alert>
                </Box>
              )}
              {!cameraHardwareLoading && cameraHardwareDevices.length > 0 && !hasHardwareCameras && (
                <Box sx={{ position: 'absolute', bottom: 8, left: 8, right: 48, zIndex: 2, maxHeight: '36%', overflow: 'auto' }}>
                  <Alert severity="warning" sx={{ py: 0.5, '& .MuiAlert-message': { py: 0 } }}>
                    Ustaw „Liczba kamer” większą od zera przy urządzeniu w Ustawieniach → Hardware.
                  </Alert>
                </Box>
              )}
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: 'grid',
                  gridTemplateColumns: `repeat(${camDetailGridCols}, 1fr)`,
                  gridAutoRows: '1fr',
                  width: '100%',
                  boxSizing: 'border-box',
                  gap: '1px',
                }}
              >
                {cameraStreamUrls.length === 0 ? (
                  <Box sx={{ border: '1px solid #222', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.45)', fontSize: 14 }}>
                    Brak strumienia
                  </Box>
                ) : (
                  cameraStreamUrls.map((u, idx) => (
                    <Box key={`${cameraDetailPlayerEpoch}-${idx}-${u}`} sx={{ border: '1px solid #222', position: 'relative', minHeight: 0 }}>
                      {/* eslint-disable-next-line @typescript-eslint/no-var-requires */}
                      {React.createElement(require('../components/common/HlsPlayer').default, { src: u, autoPlay: true, muted: true, live: true, style: { width: '100%', height: '100%', objectFit: 'contain', backgroundColor: '#000' } })}
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          </Box>
        )}
        <AddProjectForm
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onAdd={() => {}}
          onEdit={(edited: Project) => {
            try {
              setProject(toDisplayData(edited));
            } finally {
              setEditOpen(false);
            }
          }}
          editMode
          projectData={project as any}
        />
      </Box>
    </>
  );
};

export default ProjectDetailPage;


